/* eslint-disable max-lines */
import { Injectable } from '@angular/core';
import { Observable, Subscription, of, zip } from 'rxjs';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';
import isEmpty from 'lodash-es/isEmpty';
import { ParamMap } from '@angular/router';

import { BookingFacade } from '@booking/booking.facade';
import { AMPLITUDE_EVENTS, EVENT_SOURCE } from '@app/modules/core/constants/amplitude.constants';
import { SearchBy } from '@shared/base-search-bar/entities/search.entities';
import { AirValidationResponse, AirValidationBySectorResponse } from '@app/entities/air-validation.response';
import { HotelValidationResponse } from '@booking/entities/hotel-validation.response';
import { TravelBookingType } from '@pax/entities/travel-booking-types';
import { PropertiesAmplitudeService } from '@app/services/properties-amplitude.service';
import { hotelAmplitudeProductPropertiesMapperFromValidation } from '@app/modules/core/mappers/hotels-amplitude.mapper';
import { VehicleValidateResponse } from '@autos/entities/vehicle-validate.response';
import { autoAmplitudeProductPropertiesMapper } from '@autos/auto-results/mappers/auto-results.mapper';
import { EventProperties, ProductProperties, SearchProperties } from '@avo/interface/trigger-props';
import { flightAmplitudeProductPropertiesMapper } from '@app/modules/core/mappers/flights-amplitude.mapper';
import { airPackagePropsFromValidation } from '@amplitude/mappers/package-amplitude.mapper';
import { TriggerAmplitudeService } from '@amplitude/services/trigger-amplitude.service';
import { TriggerEvent } from '@avo/interface/trigger-entity';
import { AVO_EVENTS } from '@avo/events/avo.events';
import { BookingErrorEvent, ValidatorEntityCompact } from '@booking/entities/booking-amplitude-entities';
import { AllProductsValidation, BookingResultValidation } from '@app/entities/validation-types';
import { AvoService } from '@avo/services/avo.service';
import { AvoDispatchOrchestrator } from '@avo/orchestrator/avo.dispatch';
import { PaxFacade } from '@pax/pax.facade';
import { ActivitiesValidateResponse } from '@activities/entities/activities-booking';
import { activitiesPropsFromValidation } from '@amplitude/mappers/activities-amplitude.mapper';

@Injectable()
export class BookingAmplitudeService {

  protected AvoDispatch = new AvoDispatchOrchestrator();

  constructor(
    private facade: BookingFacade,
    private propertiesAmplitudeService: PropertiesAmplitudeService,
    private triggerAmplitudeService: TriggerAmplitudeService,
    private readonly avoService: AvoService,
    private readonly paxFacade: PaxFacade,
  ) { }

  private get airValidation$(): Observable<AirValidationResponse> {
    return this.facade.airValidation$;
  }
  private get airValidationBySector$(): Observable<AirValidationBySectorResponse> {
    return this.facade.airValidationBySector$;
  }
  private get hotelValidation$(): Observable<HotelValidationResponse> {
    return this.facade.hotelValidation$;
  }
  private get autoValidation$(): Observable<VehicleValidateResponse> {
    return this.facade.autoValidation$;
  }
  private get activitiesValidation$(): Observable<ActivitiesValidateResponse> {
    return this.facade.activitiesValidation$;
  }

  private hasValidation(validator: AllProductsValidation): boolean {
    return !isEmpty(validator)
  }

  public registerViewBookingScreenAmplitudeEvent(travelType: TravelBookingType, searchId: string): Subscription {
    return this.getSearchBy$(travelType).pipe(
      filter(searchType => !!searchType),
      switchMap((searchType) => zip(
        of(searchType),
        this.propertiesAmplitudeService.searchProperties$(searchType, searchId),
        this.getValidatorsByType$(),
        this.paxFacade.travelersHaveBeenLoaded$.pipe(
          filter((hasTravelers) => hasTravelers),
          take(1),
          switchMap(() => this.paxFacade.travelers$)
        )
      )),
      map(([searchType, searchProps, validators, hasTravelers]) => ({
        eventName: AVO_EVENTS.VIEW_BOOKING_SCREEN,
        eventParams: {
          TRAVEL_PRODUCT_TYPE: searchType, SOURCE: EVENT_SOURCE.BOOKING,
          HAS_TRAVELLERS: hasTravelers?.length > 0,
          RESULT: this.getBookingResultValidation(searchType, validators)
        },
        searchProps,
        productProps: this.getProductProps(validators, searchType)
      })),
      switchMap(({ eventName, eventParams, searchProps, productProps }) =>
        this.avoService.getEventTriggerProps$(eventName, eventParams, searchProps, productProps)),
      tap(trigger => {
        this.AvoDispatch.viewBookingScreen({
          ...this.avoService.avoBaseProperties(trigger),
          ...this.avoService.avoSearchProps(trigger?.SEARCH_PROPS),
          ...this.avoService.avoProductProps(trigger?.PRODUCT_PROPS),
          hasTravellers: Boolean(trigger.EVENT_PROPS.HAS_TRAVELLERS)
        });
      })
    ).subscribe();
  }

  public getBookingErrorProps$(searchType: SearchBy): Observable<BookingErrorEvent> {
    return zip(
      this.propertiesAmplitudeService.searchProperties$(searchType).pipe(take(1)),
      this.getValidatorsByType$()
    ).pipe(map(([searchProps, validators]) => ({ searchProps, productProps: this.getProductProps(validators, searchType) })));
  }

  public airCheckoutTriggerAmplitudeEntity$(
    validation: AirValidationResponse, params: ParamMap, searchProps: SearchProperties): Observable<TriggerEvent> {
    const productProps = flightAmplitudeProductPropertiesMapper(validation.TripOption);
    const eventParams = {
      SOURCE: EVENT_SOURCE.BOOKING,
      ITINERARY_ID: params.get('detailId')
    };
    return this.triggerAmplitudeService.trackAmplitudeEventProps$(
      AVO_EVENTS.PROCEED_TO_CHECKOUT,
      eventParams, searchProps, productProps
    );
  }

  public hotelCheckoutTriggerAmplitudeEntity$(
    validation: HotelValidationResponse, params: ParamMap, searchProps: SearchProperties): Observable<TriggerEvent> {
    const productProps = hotelAmplitudeProductPropertiesMapperFromValidation(validation.HotelReservation);
    const eventParams = {
      SOURCE: EVENT_SOURCE.BOOKING,
      ITINERARY_ID: params.get('detailId')
    };
    return this.triggerAmplitudeService.trackAmplitudeEventProps$(
      AVO_EVENTS.PROCEED_TO_CHECKOUT,
      eventParams, searchProps, productProps
    );
  }

  public autosCheckoutTriggerAmplitudeEntity$(
    validation: VehicleValidateResponse, params: ParamMap, searchProps: SearchProperties): Observable<TriggerEvent> {
    const productProps = autoAmplitudeProductPropertiesMapper(validation.VehicleOption);
    const eventParams = {
      SOURCE: EVENT_SOURCE.BOOKING,
      ITINERARY_ID: params.get('detailId')
    };
    return this.triggerAmplitudeService.trackAmplitudeEventProps$(
      AVO_EVENTS.PROCEED_TO_CHECKOUT,
      eventParams, searchProps, productProps
    );
  }

  public packagesCheckoutTriggerAmplitudeEntity$(hotelValidation: HotelValidationResponse, airValidation: AirValidationResponse,
    params: ParamMap, searchProps: SearchProperties): Observable<TriggerEvent> {
    const productProps = airPackagePropsFromValidation(airValidation, hotelValidation);
    const eventParams = {
      SOURCE: EVENT_SOURCE.BOOKING,
      ITINERARY_ID: params.get('detailId')
    };
    return this.triggerAmplitudeService.trackAmplitudeEventProps$(
      AVO_EVENTS.PROCEED_TO_CHECKOUT,
      eventParams, searchProps, productProps
    );
  }

  public activitiesCheckoutTriggerAmplitudeEntity$(
    optionId: string, 
    searchProps: SearchProperties,
    productProps: ProductProperties): Observable<TriggerEvent> {
    const eventParams = {
      SOURCE: EVENT_SOURCE.BOOKING,
      ITINERARY_ID: optionId
    };
    return this.triggerAmplitudeService.trackAmplitudeEventProps$(
      AVO_EVENTS.PROCEED_TO_CHECKOUT,
      eventParams, searchProps, productProps
    );
  }

  public triggerBookingWithProductProps$(
    eventType: AMPLITUDE_EVENTS, eventParams: EventProperties, type: TravelBookingType): Observable<TriggerEvent> {
    switch (type) {
      case TravelBookingType.FLIGHTS:
      case TravelBookingType.FLIGHTS_BY_SECTOR:
        return zip(this.propertiesAmplitudeService.searchProperties$(SearchBy.FLIGHTS).pipe(take(1)), this.airValidation$)
          .pipe(
            map(([searchProps, airValidation]) => ({
              eventName: eventType, searchProps,
              productProps: flightAmplitudeProductPropertiesMapper(airValidation?.TripOption)
            })),
            switchMap(({ eventName, searchProps, productProps }) =>
              this.triggerAmplitudeService.trackAmplitudeEventProps$(eventName, eventParams, searchProps, productProps)
            ));
      case TravelBookingType.BUSES:
        return zip(this.propertiesAmplitudeService.searchProperties$(SearchBy.BUSES).pipe(take(1)), this.airValidation$)
          .pipe(
            map(([searchProps, airValidation]) => ({
              eventName: eventType, searchProps,
              productProps: flightAmplitudeProductPropertiesMapper(airValidation?.TripOption)
            })),
            switchMap(({ eventName, searchProps, productProps }) =>
              this.triggerAmplitudeService.trackAmplitudeEventProps$(eventName, eventParams, searchProps, productProps)
            ));
      case TravelBookingType.HOTELS:
        return zip(this.propertiesAmplitudeService.searchProperties$(SearchBy.HOTELS).pipe(take(1)), this.hotelValidation$)
          .pipe(
            map(([searchProps, hotelValidation]) => ({
              eventName: eventType, searchProps,
              productProps: hotelAmplitudeProductPropertiesMapperFromValidation(hotelValidation?.HotelReservation)
            })),
            switchMap(({ eventName, searchProps, productProps }) =>
              this.triggerAmplitudeService.trackAmplitudeEventProps$(eventName, eventParams, searchProps, productProps)
            ));
      case TravelBookingType.AUTOS:
        return zip(this.propertiesAmplitudeService.searchProperties$(SearchBy.AUTOS).pipe(take(1)), this.autoValidation$)
          .pipe(
            map(([searchProps, autoValidation]) => ({
              eventName: eventType, searchProps,
              productProps: autoAmplitudeProductPropertiesMapper(autoValidation?.VehicleOption)
            })),
            switchMap(({ eventName, searchProps, productProps }) =>
              this.triggerAmplitudeService.trackAmplitudeEventProps$(eventName, eventParams, searchProps, productProps)
            ));
      case TravelBookingType.PACKAGE_AIR:
        return zip(
          this.propertiesAmplitudeService.searchProperties$(SearchBy.PACKAGES).pipe(take(1)),
          this.hotelValidation$, this.airValidation$
        ).pipe(
          map(([searchProps, hotelValidation, airValidation]) => ({
            eventName: eventType, searchProps,
            productProps: airPackagePropsFromValidation(airValidation, hotelValidation)
          })),
          switchMap(({ eventName, searchProps, productProps }) =>
            this.triggerAmplitudeService.trackAmplitudeEventProps$(eventName, eventParams, searchProps, productProps)
          ));
      default:
        return this.triggerAmplitudeService.trackAmplitudeEventProps$(eventType, eventParams);
    }
  }

  public getBookingResultValidation(type: SearchBy, validators: ValidatorEntityCompact): BookingResultValidation {
    switch (type) {
      case SearchBy.FLIGHTS:
      case SearchBy.BUSES:
        return validators?.air?.Result;
      case SearchBy.HOTELS:
      case SearchBy.BYHOURS:
        return validators?.hotel?.StatusChanged;
      case SearchBy.PACKAGES:
        return {
          airValidation: validators?.air?.Result,
          hotelValidation: validators?.hotel?.StatusChanged,
        };
      default:
        return null;
    }
  }

  private getSearchBy$(travelType: TravelBookingType): Observable<SearchBy> {
    return zip(
      this.airValidation$,
      this.airValidationBySector$,
      this.hotelValidation$,
      this.autoValidation$,
      this.activitiesValidation$
    ).pipe(
      map(([airValidation, airValidationBySector, hotelValidation, autoValidation, activitiesValidation]) => {
        switch(true) {
          case travelType === TravelBookingType.HOTELS && this.hasValidation(hotelValidation):
            return hotelValidation.HotelReservation.MicroStay ? SearchBy.BYHOURS: SearchBy.HOTELS; 
          case travelType === TravelBookingType.AUTOS && this.hasValidation(autoValidation):
            return SearchBy.AUTOS;
          case travelType === TravelBookingType.FLIGHTS && this.hasValidation(airValidation):
            return SearchBy.FLIGHTS;
          case travelType === TravelBookingType.BUSES && this.hasValidation(airValidation):
            return SearchBy.BUSES;
          case travelType === TravelBookingType.FLIGHTS_BY_SECTOR && this.hasValidation(airValidationBySector):
            return SearchBy.FLIGHTS_BY_SECTOR;
          case travelType === TravelBookingType.PACKAGE_AIR && 
              this.hasValidation(airValidation) && this.hasValidation(hotelValidation):
            return SearchBy.PACKAGES;
          case travelType === TravelBookingType.ACTIVITIES && this.hasValidation(activitiesValidation):
            return SearchBy.ACTIVITIES;
          default:
            return null;
        }
      })
    )
  }

  private getValidatorsByType$(): Observable<ValidatorEntityCompact> {
    return zip(
      this.hotelValidation$,
      this.airValidation$,
      this.airValidationBySector$,
      this.autoValidation$,
      this.activitiesValidation$
    ).pipe(
      map(([hotel, air, airBySector, autos, activities]) => ({hotel, air, airBySector, autos, activities}))
    )
  }

  private getProductProps(validators: ValidatorEntityCompact, searchType: SearchBy): ProductProperties {
    switch (searchType) {
      case SearchBy.HOTELS:
      case SearchBy.BYHOURS:
        return hotelAmplitudeProductPropertiesMapperFromValidation(validators?.hotel?.HotelReservation);
      case SearchBy.FLIGHTS:
      case SearchBy.BUSES:
        return flightAmplitudeProductPropertiesMapper(validators?.air?.TripOption);
      case SearchBy.FLIGHTS_BY_SECTOR:
        return flightAmplitudeProductPropertiesMapper(validators?.airBySector?.AirValidateResponse[0].TripOption);
      case SearchBy.AUTOS:
        return autoAmplitudeProductPropertiesMapper(validators?.autos?.VehicleOption);
      case SearchBy.PACKAGES:
        return airPackagePropsFromValidation(validators?.air, validators?.hotel);
      case SearchBy.ACTIVITIES:
        return activitiesPropsFromValidation(validators?.activities);
      default:
        return null;
    }
  }
}
